import type { MouseEvent } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';

import { PictoEspaceAssure } from '@/assets/icons-april-fr/pictos';
import Text from '@/components/forms/text';
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { AUTH_GRAVITEE_ENABLED, HOST_MB } from '@/lib/constants';
import { useResetPassword } from '@/hooks/auth/use-auth-recovery';
import usePageTitle from '@/hooks/use-page-title';
import { nouveauMdpSchema, type NouveauMdpValues } from '@/schemas/nouveau-mdp';
import { useUiStore } from '@/stores/ui-store';
import { initiateGraviteeLogin } from '@/lib/auth/gravitee';
import { PASSWORD_RULES } from '@/lib/auth/password-rules';

const NouveauMdp = () => {
    const params = useParams();
    const { resetPassword, login: loginMutation } = useResetPassword();
    const loading = resetPassword.isPending || loginMutation.isPending;
    const login = useUiStore((state) => state.login);

    // Récupérer token et email depuis l'URL (format nouveau: /login/nouveau-mot-de-passe?token=xxx&email=xxx)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token') || params.token;
    const emailFromUrl = urlParams.get('email');

    const postResetPassword = (form: NouveauMdpValues) =>
        resetPassword.mutate({
            ...form,
            token,
            owner_belair_id: HOST_MB,
        });

    usePageTitle('Nouveau mot de passe');

    const { register, handleSubmit, watch, formState: { errors } } = useForm<NouveauMdpValues>({
        defaultValues: { email: emailFromUrl || '', password: '', password_confirmation: '' },
        resolver: zodResolver(nouveauMdpSchema) as unknown as Resolver<NouveauMdpValues>,
    });

    if (login){
        window.location.href = '/'
        return null
    }

    const handleBack = (e: MouseEvent<HTMLAnchorElement>) => {
        if (AUTH_GRAVITEE_ENABLED) {
            e.preventDefault()
            void initiateGraviteeLogin()
        }
    }

    return (
        <form onSubmit={handleSubmit(values => postResetPassword(values))}>

            <h1 className={'h2 mb-6 flex items-center gap-2'}><Link to={'/login'} onClick={handleBack} aria-label="Retour à la connexion"><i className="bi bi-arrow-left-circle mr-2" aria-hidden="true" /></Link><PictoEspaceAssure style={{width: 28, height: 28, flexShrink: 0}} />Je n'ai pas mon <span className={'text-[oklch(from_var(--color-primary)_calc(l*0.85)_c_h)]'}>mot de passe</span> ou je l'ai <span className={'text-[oklch(from_var(--color-primary)_calc(l*0.85)_c_h)]'}>oublié</span></h1>

            <p className={'inline-block mt-2 mb-4'}>Choisissez un <b>nouveau mot de passe sécurisé</b> pour votre compte.</p>

            <div className={'mb-6 mt-2'}>
                <Text
                    type={'email'}
                    autoComplete="email"
                    {...register('email')}
                    value={watch('email')}
                    error={errors.email?.message}
                    required
                    disabled={!!watch('email')}
                    label={'Votre adresse mail personnelle'}
                />
            </div>

            <div className="mb-4">
                <small className="text-muted-foreground">
                    Le mot de passe doit contenir :
                    <ul className="mb-0 mt-2" style={{fontSize: '12px'}}>
                        {PASSWORD_RULES.map(rule => <li key={rule}>{rule}</li>)}
                    </ul>
                </small>
            </div>

            <div className={'mb-6 mt-2'}>
                <Text
                    type={'password'}
                    autoComplete="new-password"
                    {...register('password')}
                    value={watch('password')}
                    error={errors.password?.message}
                    required
                    label={'Votre nouveau mot de passe ( 12 caractères minimum )'}
                />
            </div>

            <div className={'mb-6 mt-2'}>
                <Text
                    type={'password'}
                    autoComplete="new-password"
                    {...register('password_confirmation')}
                    value={watch('password_confirmation')}
                    error={errors.password_confirmation?.message}
                    required
                    label={'Confirmez votre nouveau mot de passe'}
                />
            </div>

            <div className="grid gap-2">
                {
                    loading ? <SmallSpinner /> : <button type="submit" className={'inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[0.875rem]/5 font-medium rounded-md transition-all ease-[ease] cursor-pointer no-underline disabled:opacity-50 disabled:pointer-events-none bg-primary text-primary-foreground border-none hover:bg-primary-hover'}>
                        Je définis mon mot de passe
                    </button>
                }
            </div>

        </form>
    );
};

export default NouveauMdp;
